/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  IconButton,
  Card,
  CardContent,
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  Chip,
  useMediaQuery,
  CircularProgress as MUICircularProgress,
} from "@material-ui/core";

import {
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Description as DescriptionIcon,
  TimerOff as TimerOffIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  PauseCircleOutline as PauseCircleOutlineIcon,
  HelpOutline as HelpOutlineIcon,
} from "@material-ui/icons";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";
import CampaignModalPhrase from "../../components/CampaignModalPhrase";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import {
  AddCircle,
  Build,
  DevicesFold,
  TextFields as TextFieldsIcon,
  ContentCopy,
} from "@mui/icons-material";

import {
  Button,
  CircularProgress,
  Grid,
  Stack,
} from "@mui/material";

import {
  colorBackgroundTable,
  colorLineTable,
  colorLineTableHover,
  colorTopTable
} from "../../styles/styles";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    const newCampaigns = [];

    if (isArray(campaigns)) {
      campaigns.forEach(campaign => {
        const campaignIndex = state.findIndex(u => u.id === campaign.id);
        if (campaignIndex !== -1) {
          state[campaignIndex] = campaign;
        } else {
          newCampaigns.push(campaign);
        }
      });
    }

    return [...state, ...newCampaigns];
  }

  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    const campaignIndex = state.findIndex(u => u.id === campaign.id);

    if (campaignIndex !== -1) {
      state[campaignIndex] = campaign;
      return [...state];
    } else {
      return [campaign, ...state];
    }
  }

  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;

    const campaignIndex = state.findIndex(u => u.id === campaignId);
    if (campaignIndex !== -1) {
      state.splice(campaignIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    width: "100%",
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    backgroundColor: theme.palette.type === 'dark' ? "#1e1e1e" : "#ffffff",
    ...theme.scrollbarStyles,
    border: "none",
  },
  headerContainer: {
    background: "linear-gradient(90deg, #6517ab 0%, #8e44ad 100%)",
    padding: theme.spacing(3),
    borderRadius: "0 0 20px 20px",
    color: "white",
    marginBottom: theme.spacing(3),
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  },
  headerTitle: {
    fontSize: "2rem",
    fontWeight: 800,
    marginBottom: theme.spacing(1),
  },
  headerSubtitle: {
    fontSize: "1rem",
    opacity: 0.9,
    marginBottom: theme.spacing(2),
  },
  searchPaper: {
    padding: "4px 16px",
    display: "flex",
    alignItems: "center",
    borderRadius: "15px",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    "& .MuiInputBase-root": {
      color: "white",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "rgba(255, 255, 255, 0.7)",
      opacity: 1,
    },
  },
  addButton: {
    background: theme.palette.background.paper,
    color: "#6517ab",
    borderRadius: "12px",
    padding: "10px 24px",
    fontWeight: "700",
    textTransform: "none",
    transition: "all 0.3s ease",
    "&:hover": {
      background: theme.palette.type === "light" ? "#f0f0f0" : "rgba(255, 255, 255, 0.1)",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
    },
  },
  tabContainer: {
    marginTop: theme.spacing(2),
  },
  tabs: {
    minHeight: "40px",
    "& .MuiTabs-indicator": {
      backgroundColor: "white",
      height: "3px",
      borderRadius: "3px",
    },
  },
  tab: {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "1rem",
    color: "rgba(255, 255, 255, 0.7)",
    minWidth: "120px",
    minHeight: "40px",
    "&.Mui-selected": {
      color: "white",
    },
  },
  premiumCard: {
    borderRadius: "15px",
    transition: "all 0.3s ease",
    backgroundColor: theme.palette.type === 'dark' ? "#1e1e1e" : "#ffffff",
    border: `1px solid ${theme.palette.type === 'dark' ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"}`,
    "&:hover": {
      boxShadow: "0 8px 30px rgba(101, 23, 171, 0.15)",
      transform: "translateY(-5px)",
    },
  },
  iconButton: {
    borderRadius: "10px",
    padding: "8px",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  tableRow: {
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: theme.palette.type === 'dark' ? "rgba(255, 255, 255, 0.02)" : "rgba(101, 23, 171, 0.05)",
    },
    cursor: "pointer",
  },
  statusChip: {
    fontWeight: 700,
    borderRadius: "8px",
  },
}));

const CampaignsPhrase = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);

  const [campaignflows, setCampaignFlows] = useState([]);
  const [ModalOpenPhrase, setModalOpenPhrase] = useState(false);
  const [campaignflowSelected, setCampaignFlowSelected] = useState();
  const [tabValue, setTabValue] = useState("table");

  const handleDeleteCampaign = async campaignId => {
    try {
      await api.delete(`/flowcampaign/${campaignId}`);
      toast.success("Frase deletada");
      getCampaigns()
    } catch (err) {
      toastError(err);
    }
    
  };

  const getCampaigns =  async() => {
    setLoading(true);
    await api.get("/flowcampaign").then(res => {
      setCampaignFlows(res.data.flow);
      setLoading(false);
    });
  };

  const onSaveModal = () => {
    getCampaigns()
  }

  const handleScroll = e => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const renderCardActions = (flow) => (
    <Box display="flex" gap={1} justifyContent="center" width="100%" mt={tabValue === "grid" ? 2 : 0}>
      <IconButton
        size="small"
        onClick={() => {
          setCampaignFlowSelected(flow.id);
          setModalOpenPhrase(true);
        }}
        style={{ backgroundColor: theme.palette.type === 'dark' ? "rgba(25, 118, 210, 0.1)" : "#e3f2fd", color: "#1976d2" }}
        className={classes.iconButton}
        title="Editar"
      >
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => {
          setDeletingContact(flow);
          setConfirmModalOpen(true);
        }}
        style={{ backgroundColor: theme.palette.type === 'dark' ? "rgba(211, 47, 47, 0.1)" : "#ffebee", color: "#d32f2f" }}
        className={classes.iconButton}
        title="Excluir"
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  useEffect(() => {
    getCampaigns();
  }, []);

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingCampaign &&
          `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${
            deletingCampaign.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCampaign(deletingContact.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <CampaignModalPhrase
        open={ModalOpenPhrase}
        onClose={() => setModalOpenPhrase(false)}
        FlowCampaignId={campaignflowSelected}
        onSave={onSaveModal}
      />
      <Box className={classes.headerContainer}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h5" className={classes.headerTitle}>Campanhas de Frases</Typography>
            <Typography className={classes.headerSubtitle}>Gerencie suas automações baseadas em palavras-chave</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" gap={2} flexWrap="wrap" justifyContent={isMobile ? "center" : "flex-end"}>
              <Paper className={classes.searchPaper} elevation={0}>
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "rgba(255,255,255,0.7)" }} />
                </InputAdornment>
                <TextField
                  placeholder="Pesquisar..."
                  value={searchParam}
                  onChange={handleSearch}
                  InputProps={{ disableUnderline: true }}
                />
              </Paper>
              <Button
                variant="contained"
                className={classes.addButton}
                startIcon={<AddCircle />}
                onClick={() => {
                  setCampaignFlowSelected();
                  setModalOpenPhrase(true);
                }}
              >
                Nova Campanha
              </Button>
            </Box>
          </Grid>
        </Grid>

        <div className={classes.tabContainer}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            className={classes.tabs}
          >
            <Tab label="Lista" value="table" className={classes.tab} />
            <Tab label="Grade" value="grid" className={classes.tab} />
          </Tabs>
        </div>
      </Box>

      <Box px={2} pb={3}>
        <Paper className={classes.premiumCard} style={{ padding: theme.spacing(3), backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#f8f9fa", border: "1px dashed #6517ab" }}>
          <Box display="flex" alignItems="center" mb={2} gap={1}>
            <HelpOutlineIcon style={{ color: "#6517ab" }} />
            <Typography variant="h6" style={{ fontWeight: 800, color: "#6517ab" }}>
              Como funciona o acionamento por frases?
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Box display="flex" gap={1.5} alignItems="flex-start">
                <Avatar style={{ backgroundColor: "#6517ab", width: 28, height: 28, fontSize: "0.9rem", fontWeight: 700 }}>1</Avatar>
                <Box>
                  <Typography variant="subtitle2" style={{ fontWeight: 700 }}>Criar Campanha</Typography>
                  <Typography variant="caption" color="textSecondary">Dê um nome para organizar internamente.</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" gap={1.5} alignItems="flex-start">
                <Avatar style={{ backgroundColor: "#6517ab", width: 28, height: 28, fontSize: "0.9rem", fontWeight: 700 }}>2</Avatar>
                <Box>
                  <Typography variant="subtitle2" style={{ fontWeight: 700 }}>Definir Frase</Typography>
                  <Typography variant="caption" color="textSecondary">Adicione <b>uma</b> palavra-chave que o cliente deve enviar.</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" gap={1.5} alignItems="flex-start">
                <Avatar style={{ backgroundColor: "#6517ab", width: 28, height: 28, fontSize: "0.9rem", fontWeight: 700 }}>3</Avatar>
                <Box>
                  <Typography variant="subtitle2" style={{ fontWeight: 700 }}>Vincular Fluxo</Typography>
                  <Typography variant="caption" color="textSecondary">Escolha qual robô (FlowBuilder) será disparado.</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" gap={1.5} alignItems="flex-start">
                <Avatar style={{ backgroundColor: "#6517ab", width: 28, height: 28, fontSize: "0.9rem", fontWeight: 700 }}>4</Avatar>
                <Box>
                  <Typography variant="subtitle2" style={{ fontWeight: 700 }}>Ativar</Typography>
                  <Typography variant="caption" color="textSecondary">Ao receber a frase exata, o fluxo iniciará no ato!</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box mt={2} p={1.5} style={{ backgroundColor: "rgba(101, 23, 171, 0.05)", borderRadius: "10px", borderLeft: "4px solid #6517ab" }}>
            <Typography variant="caption" style={{ color: "#6517ab", fontWeight: 600 }}>
              💡 Importante: Cada campanha aceita <b>apenas uma frase exata</b>. Para ativar o robô com diferentes palavras (ex: "oi" e "ola"), crie uma campanha para cada uma delas.
            </Typography>
          </Box>
        </Paper>
      </Box>

      <Paper
        className={classes.mainPaper}
        elevation={0}
        onScroll={handleScroll}
      >
        {tabValue === "table" && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome da Campanha</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaignflows
                .filter(flow => flow.name.toLowerCase().includes(searchParam))
                .map((flow) => (
                <TableRow key={flow.id} className={classes.tableRow}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      <Avatar style={{ backgroundColor: "#6517ab", width: 40, height: 40 }}>
                        <TextFieldsIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body1" style={{ fontWeight: 600, color: theme.palette.text.primary }}>
                          {flow.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {flow.id}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={flow.status ? "Ativo" : "Desativado"}
                      size="small"
                      className={classes.statusChip}
                      style={{
                        backgroundColor: flow.status ? "rgba(16, 185, 129, 0.1)" : "rgba(107, 114, 128, 0.1)",
                        color: flow.status ? "#10b981" : "#6b7280",
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {renderCardActions(flow)}
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={3} />}
            </TableBody>
          </Table>
        )}

        {tabValue === "grid" && (
          <Grid container spacing={3} style={{ padding: "16px" }}>
            {campaignflows
              .filter(flow => flow.name.toLowerCase().includes(searchParam))
              .map((flow) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={flow.id}>
                <Card className={classes.premiumCard}>
                  <CardContent style={{ textAlign: 'center' }}>
                    <Box display="flex" justifyContent="center" mb={2}>
                      <Avatar style={{ backgroundColor: "#6517ab", width: 60, height: 60 }}>
                        <TextFieldsIcon style={{ fontSize: 30 }} />
                      </Avatar>
                    </Box>
                    <Typography variant="h6" style={{ fontWeight: 700, color: theme.palette.text.primary }} gutterBottom>
                      {flow.name}
                    </Typography>
                    <Box display="flex" justifyContent="center" mt={1} mb={2}>
                      <Chip
                        label={flow.status ? "Ativo" : "Desativado"}
                        size="small"
                        className={classes.statusChip}
                        style={{
                          backgroundColor: flow.status ? "rgba(16, 185, 129, 0.1)" : "rgba(107, 114, 128, 0.1)",
                          color: flow.status ? "#10b981" : "#6b7280",
                        }}
                      />
                    </Box>
                    <Divider style={{ margin: '16px 0' }} />
                    <Typography variant="caption" color="textSecondary" display="block">
                      ID: <b>{flow.id}</b>
                    </Typography>
                    {renderCardActions(flow)}
                  </CardContent>
                </Card>
              </Grid>
            ))}
            {loading && (
              <Grid item xs={12} align="center">
                <CircularProgress color="primary" />
              </Grid>
            )}
          </Grid>
        )}
      </Paper>
    </MainContainer>
  );
};

export default CampaignsPhrase;
